// Dependencies
import { z } from "zod";

import { SIDE_NAV_VOCABULARY, coercedBoolean } from "@performyard/utils-isomorphic";

// local imports
import request from "../base-request";

const sideNavThemeSchema = z.object({
	id: z.string(),
	name: z.string(),
	backgroundColor: z.string(),
	foregroundColor: z.string(),
	isDefault: coercedBoolean,
});

export const SIDE_NAV_AREAS = SIDE_NAV_VOCABULARY.areas;
export const SIDE_NAV_TAGS = SIDE_NAV_VOCABULARY.tags;

export type SideNavArea = (typeof SIDE_NAV_AREAS)[number];
export type SideNavTag = (typeof SIDE_NAV_TAGS)[number];
export type VisibleNavTags = Partial<Record<SideNavArea, SideNavTag[]>> & Record<string, string[]>;

const visibleNavTagsSchema = z.record(z.string(), z.array(z.string())).transform((tags) => tags as VisibleNavTags);

export const navigationConfigSchema = z.object({
	currentUserId: z.string(),
	currentUserName: z.string(),
	currentUserJobTitle: z.string(),
	currentUserEmail: z.string().optional().default(""),
	companyId: z.string(),
	theUserId: z.string(),
	isViewingSelf: coercedBoolean,
	currentNav: z.string().nullable(),
	isAdmin: coercedBoolean,
	isBasicAdmin: coercedBoolean,
	isBillingAdmin: coercedBoolean.optional().default(false),
	isEngagementOnly: coercedBoolean,
	isMeetingsEnabled: coercedBoolean.optional().default(false),
	isEngagementEnabled: coercedBoolean.optional().default(false),
	isSurveysEnabled: coercedBoolean.optional().default(false),
	isOverlord: coercedBoolean,
	hasDirectReports: coercedBoolean.optional().default(false),
	possessor: z
		.object({
			name: z.string(),
			id: z.string(),
		})
		.nullable(),
	visibleNavTags: visibleNavTagsSchema.optional(),
	navColor: z.string().optional().default(""),
	iconColor: z.string().optional().default(""),
	preferredNavigation: z.enum(["classic", "new"]).optional().default("classic"),
	sideNavTheme: sideNavThemeSchema.optional(),
	currentPromotion: z.record(z.string(), z.unknown()).optional().default({}),
	logoUrl: z.string().optional().default(""),
	mobileLogoUrl: z.string().optional().default(""),
});

export const navigationConfigResponseSchema = z.object({
	navigationConfig: navigationConfigSchema,
});

export type NavigationConfig = z.infer<typeof navigationConfigSchema>;
export type NavigationConfigResponse = z.infer<typeof navigationConfigResponseSchema>;

/**
 * Whether the server exposed a nav area (and optionally a tag within it) to this user. Omit `tag`
 * to ask only whether the area is present. Missing area means not visible, so gates fail closed.
 */
export const hasVisibleTag = (
	config: Pick<NavigationConfig, "visibleNavTags">,
	area: SideNavArea,
	tag?: SideNavTag
): boolean => {
	const tagsForArea = config.visibleNavTags?.[area];
	if (tagsForArea === undefined) return false;
	if (tag === undefined) return true;
	return tagsForArea.includes(tag);
};

/**
 * Get navigation config for current viewing context.
 * Optionally pass employeeId to get config for viewing a specific employee.
 */
export const getNavigationConfig = async (employeeId?: string) => {
	const url = employeeId ? `/api/navigation-config?employeeId=${employeeId}` : "/api/navigation-config";
	const [data] = await request(url);
	return navigationConfigResponseSchema.parse(data);
};
